import React, { useState, useEffect } from 'react';
import { Terminal, Power, Layers, Server, Users, User, Menu, X, ShoppingCart, Paintbrush, Newspaper, Shield } from 'lucide-react';

const GlitchText = ({ text, className }) => {
  const [glitchActive, setGlitchActive] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setGlitchActive(true);
      setTimeout(() => setGlitchActive(false), Math.random() * 300 + 100);
    }, Math.random() * 4000 + 2000);
    return () => clearInterval(interval);
  }, []);

  return (
    <span
      className={`
        relative inline-block ${className} text-slate-100
        before:content-[attr(data-text)] before:absolute before:left-[2px] before:text-purple-300/60
        after:content-[attr(data-text)] after:absolute after:left-[-2px] after:text-indigo-400/60
        ${glitchActive ? 'animate-glitch' : ''}
      `}
      data-text={text}
    >
      {text}
    </span>
  );
};

const Navbar = ({ currentPage }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const response = await fetch('https://api.4fingerstudios.com/api/auth/me', {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const userData = await response.json();
            setIsAuthenticated(true);
            setIsAdmin(userData.role === 'Admin');
          } else {
            setIsAuthenticated(false);
            setIsAdmin(false);
          }
        } catch (error) {
          setIsAuthenticated(false);
          setIsAdmin(false);
          console.error('Auth check failed:', error);
        }
      } else {
        setIsAuthenticated(false);
        setIsAdmin(false);
      }
    };
    checkAuth();
  }, []);

  const baseNavItems = [
    { icon: <Terminal size={20} />, href: '/', title: 'HOME' },
    { icon: <Paintbrush size={20} />, href: '/Art', title: 'ART' },
    { icon: <Layers size={20} />, href: '/Projects', title: 'PROJECTS' },
    { icon: <Server size={20} />, href: '/Mods', title: 'MODS' },
    { icon: <Newspaper size={20} />, href: '/News', title: 'NEWS' },
    { icon: <ShoppingCart size={20} />, href: '/Store', title: 'SHOP' },
    { icon: <Users size={20} />, href: '/Partners', title: 'PARTNERS' },
    { icon: <User size={20} />, href: '/Team', title: 'TEAM' },
    { 
      icon: <img src="https://cdn.4fingerstudios.com/Necrova/NecrovaStuffas/Full.png" width={20} height={20} alt="Necrova Logo" />, 
      href: '/Necrova-Industries', 
      title: 'NECROVA' 
    },
    { icon: <Power size={20} />, href: isAuthenticated ? '/user-profile' : '/Profile', title: isAuthenticated ? 'PROFILE' : 'LOGIN' }
  ];

  const navItems = isAdmin
    ? [...baseNavItems.slice(0, -1), { icon: <Shield size={20} />, href: '/admin', title: 'ADMIN' }, baseNavItems[baseNavItems.length - 1]]
    : baseNavItems;

  return (
    <nav className="fixed top-0 w-full z-50 bg-gradient-to-r from-gray-900 via-slate-900 to-gray-900 shadow-md shadow-slate-900/50">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between py-4 px-6">
          <div className="flex items-center space-x-3">
            <GlitchText text="4FS" className="text-3xl font-black hover:text-purple-300 transition-colors duration-300" />
            <div className="h-6 w-px bg-slate-700"></div>
            <span className="text-sm font-medium text-slate-500 uppercase tracking-wider">{currentPage}</span>
          </div>

          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden text-slate-400 hover:text-white transition-colors duration-300"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          <div className="hidden md:block">
            <ul className="flex items-center space-x-10">
              {navItems.map((item, index) => (
                <li
                  key={index}
                  onMouseEnter={() => setActiveIndex(index)}
                  onMouseLeave={() => setActiveIndex(null)}
                  className="group relative"
                >
                  <a href={item.href} className="flex flex-col items-center">
                    <div className={`p-2 transition-all duration-300 rounded-lg
                      ${index === activeIndex ? 'bg-slate-800/50 text-white' : 'text-slate-400 hover:text-white'}`}
                    >
                      {item.icon}
                    </div>
                    <span
                      className={`absolute -bottom-4 text-xs font-medium tracking-wide
                        transition-all duration-300 transform
                        ${index === activeIndex ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-1'}
                      `}
                    >
                      {item.title}
                    </span>
                  </a>
                  <div className={`absolute bottom-0 left-1/2 transform -translate-x-1/2 w-0 h-0.5
                    bg-gradient-to-r from-indigo-500 to-purple-500 transition-all duration-300
                    ${index === activeIndex ? 'w-full' : 'w-0'}`}
                  ></div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className={`md:hidden transition-all duration-300 ease-in-out overflow-hidden border-t border-slate-800/50
          ${isMenuOpen ? 'max-h-150 opacity-100' : 'max-h-0 opacity-0'}`}
        >
          <ul className="flex flex-col p-4 space-y-3">
            {navItems.map((item, index) => (
              <li key={index} className="transform transition-all duration-300"
                style={{ transitionDelay: `${index * 50}ms` }}>
                <a
                  href={item.href}
                  className="flex items-center gap-3 p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800/30 transition-all duration-300"
                >
                  {item.icon}
                  <span>{item.title}</span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;